import { ArrowRight } from "@phosphor-icons/react";
import { C } from "@/brand/tokens";
import { PHOTOS } from "@/constants/photos";
import { ChamberChrome } from "@/components/common/ChamberChrome";

/**
 * HeroSection — Deep Mineral Blue chamber (I. THE ENTRY).
 *
 * Asymmetric composition with the right-bleed portrait and the quote
 * card LOCKED INTO the portrait's left edge — the card reads as a
 * drawer pulled out of the chamber wall, not as a floating overlay.
 *
 * Seam architecture (text rail → portrait):
 *   1. Slate Olive "doorframe" strip (6px wide) immediately left of seam
 *   2. 1px brass hairline AT the seam
 *   3. Inset shadow on the portrait's left edge for depth
 *   4. NO soft gradient — the edge is deliberately sharp
 */
export function HeroSection() {
  // Portrait bleed width as a CSS variable for the seam calculations
  const PORTRAIT_W = "44%";

  return (
    <section
      data-testid="hero"
      style={{
        background: C.surfacePrimary,
        color: C.textOnDark,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <ChamberChrome
        numeral="I."
        title="The Entry"
        category="Executive Advisory"
        meta="MMXXVI"
        tone="dark"
      />

      {/* ── Body — bleed portrait on right, text rail on left ───────── */}
      <div className="relative">
        {/* ── Slate Olive "doorframe" — visible only on lg+ ─────────── */}
        <span
          aria-hidden="true"
          className="hidden lg:block absolute top-0 bottom-0"
          style={{
            right: `calc(${PORTRAIT_W} + 1px)`, // immediately left of seam (after brass hairline)
            width: 6,
            background: C.supportOrganic,
          }}
        />
        {/* ── Brass hairline at the seam ────────────────────────────── */}
        <span
          aria-hidden="true"
          className="hidden lg:block absolute top-0 bottom-0"
          style={{
            right: PORTRAIT_W,
            width: 1,
            background: C.accentMetal,
          }}
        />

        {/* ── PORTRAIT — absolutely positioned, bleeds to right edge ──── */}
        <figure
          className="hidden lg:block absolute top-0 right-0 bottom-0"
          style={{
            width: PORTRAIT_W,
            // Sharp left edge with inset shadow for chamber-recess depth
            boxShadow: "inset 40px 0 60px -32px rgba(22,24,27,0.78)",
          }}
        >
          <div
            className="absolute inset-0"
            style={{ background: C.supportOrganic }}
          />
          <img
            src={PHOTOS.warmCloseUp}
            alt="Katrina Bernard, Founder & Principal of Faith 1st"
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              objectPosition: "center 22%",
              filter: "saturate(0.82) contrast(1.05)",
            }}
            loading="eager"
          />
          {/* Bottom darken keeps the right-edge marker legible */}
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `linear-gradient(180deg, rgba(22,24,27,0) 55%, rgba(22,24,27,0.55) 100%)`,
            }}
          />

          {/* Brass marker rotated 90° on the far-right viewport edge */}
          <div
            data-testid="hero-proof-marker"
            className="absolute right-0 top-0 bottom-0 flex items-center"
            style={{ width: 36 }}
          >
            <div
              className="flex items-center justify-center"
              style={{
                writingMode: "vertical-rl",
                transform: "rotate(180deg)",
                height: "100%",
                width: "100%",
                background: C.accentMetal,
                color: C.bgDark,
                padding: "32px 0",
              }}
            >
              <span
                className="font-inter text-[10px] uppercase font-bold tabular-nums"
                style={{ letterSpacing: "0.42em", color: C.bgDark }}
              >
                Two&nbsp;decades&nbsp;of&nbsp;practice
                <span style={{ margin: "0 14px" }}>·</span>
                Est.&nbsp;Faith&nbsp;1st
              </span>
            </div>
          </div>

          {/* ── QUOTE CARD — locked flush to the portrait's LEFT edge ── */}
          <figcaption
            data-testid="hero-quote-panel"
            className="hidden lg:block absolute"
            style={{
              right: "100%", // right edge flush against the figure's left edge (the seam)
              top: "62%",
              width: 296,
              background: C.bgDark,
              borderTop: `1px solid ${C.accentMetal}`,
              borderLeft: `1px solid ${C.brassLine}`,
              borderBottom: `1px solid ${C.brassLine}`,
              // No right border — locked into the brass seam at the portrait edge
              padding: "22px 26px",
              color: C.textOnDark,
              boxShadow: "-28px 28px 60px -28px rgba(0,0,0,0.55)",
            }}
          >
            {/* Tiny brass tab on the LEFT edge of the card */}
            <span
              aria-hidden="true"
              className="absolute"
              style={{
                left: -1,
                top: 14,
                width: 18,
                height: 1,
                background: C.accentMetal,
                transform: "translateX(-18px)",
              }}
            />
            <p
              className="font-inter text-[9.5px] uppercase font-bold mb-3"
              style={{ color: C.accentMetal, letterSpacing: "0.30em" }}
            >
              Evidence
            </p>
            <p
              className="font-cormorant leading-[1.32]"
              style={{ fontSize: "1.1rem", color: C.textOnDark }}
            >
              “Warm in conversation. Composed when the stakes get high.”
            </p>
            <div
              className="mt-5 pt-4 flex items-center gap-3"
              style={{ borderTop: `1px solid ${C.brassSoft}` }}
            >
              <span
                aria-hidden="true"
                className="inline-block h-px"
                style={{ background: C.accentMetal, width: 18 }}
              />
              <p
                className="font-inter text-[9.5px] uppercase font-semibold"
                style={{ color: C.accentMetal, letterSpacing: "0.30em" }}
              >
                Katrina Bernard, JD&nbsp;|&nbsp;CPC
              </p>
            </div>
          </figcaption>
        </figure>

        {/* ── TEXT RAIL ─────────────────────────────────────────────── */}
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-14 pt-20 lg:pt-28 pb-24 lg:pb-32 relative">
          <div className="lg:max-w-[640px]">
            <div className="flex items-center gap-4 mb-10">
              <span
                aria-hidden="true"
                className="inline-block h-px"
                style={{ background: C.accentMetal, width: 56 }}
              />
              <span
                className="font-inter text-[10.5px] uppercase font-semibold tabular-nums"
                style={{ color: C.accentMetal, letterSpacing: "0.32em" }}
              >
                § 01 &nbsp;·&nbsp; Exec. Advisory
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-cormorant"
              style={{
                color: C.textOnDark,
                fontSize: "clamp(2.6rem, 6vw, 5.4rem)",
                fontWeight: 400,
                lineHeight: 0.94,
                letterSpacing: "-0.018em",
              }}
            >
              Executive advisory
              <br />
              for the people decisions
              <br />
              that shape
              <br />
              everything
              <br />
              else.
            </h1>

            <div
              className="mt-14 max-w-[40ch]"
              style={{
                borderLeft: `1px solid ${C.accentMetal}`,
                paddingLeft: 22,
              }}
            >
              <p
                data-testid="hero-subcopy"
                className="font-inter leading-[1.7]"
                style={{
                  color: C.whiteOnDark,
                  fontSize: "clamp(0.98rem, 1.05vw, 1.05rem)",
                }}
              >
                Faith&nbsp;1st is a private advisory practice for executive
                directors, boards, and HR leaders carrying the high-stakes
                people, leadership, and culture decisions that shape
                mission-driven organizations.
              </p>
            </div>

            <div className="mt-12 flex flex-col items-start gap-3.5">
              <a
                href="#contact"
                data-testid="hero-primary-cta"
                className="group inline-flex items-center justify-between gap-5 px-7 py-4 font-inter text-[12px] uppercase font-semibold transition-colors"
                style={{
                  background: C.accentMetal,
                  color: C.bgDark,
                  letterSpacing: "0.24em",
                  minWidth: 320,
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = C.textOnDark)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = C.accentMetal)
                }
              >
                Begin a confidential inquiry
                <ArrowRight
                  size={14}
                  weight="bold"
                  className="group-hover:translate-x-1 transition-transform"
                />
              </a>
              <a
                href="#how-we-help"
                data-testid="hero-secondary-cta"
                className="group inline-flex items-center gap-3 pb-1.5 font-inter text-[11px] uppercase font-semibold border-b transition-colors"
                style={{
                  color: C.textOnDark,
                  borderColor: C.brassLine,
                  letterSpacing: "0.24em",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = C.accentMetal;
                  e.currentTarget.style.borderColor = C.accentMetal;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = C.textOnDark;
                  e.currentTarget.style.borderColor = C.brassLine;
                }}
              >
                See how an engagement begins
                <ArrowRight
                  size={12}
                  weight="bold"
                  className="group-hover:translate-x-1 transition-transform"
                />
              </a>
            </div>
          </div>

          {/* Mobile-only portrait — stacks below text */}
          <div className="lg:hidden mt-16">
            <figure className="relative">
              <div
                className="relative overflow-hidden"
                style={{
                  aspectRatio: "4 / 5",
                  background: C.supportOrganic,
                  border: `1px solid ${C.brassSoft}`,
                }}
              >
                <img
                  src={PHOTOS.warmCloseUp}
                  alt="Katrina Bernard, Founder & Principal of Faith 1st"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{
                    objectPosition: "center 22%",
                    filter: "saturate(0.82)",
                  }}
                  loading="eager"
                />
                <div
                  aria-hidden="true"
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: `linear-gradient(180deg, rgba(22,24,27,0) 55%, rgba(22,24,27,0.55) 100%)`,
                  }}
                />
              </div>
              <figcaption
                className="mt-5 p-5"
                style={{
                  background: C.bgDark,
                  border: `1px solid ${C.brassLine}`,
                }}
              >
                <p
                  className="font-cormorant leading-[1.35]"
                  style={{ fontSize: "1rem", color: C.textOnDark }}
                >
                  “Warm in conversation. Composed when the stakes get high.”
                </p>
                <p
                  className="mt-3 font-inter text-[9.5px] uppercase font-semibold"
                  style={{ color: C.accentMetal, letterSpacing: "0.30em" }}
                >
                  Katrina Bernard, JD&nbsp;|&nbsp;CPC
                </p>
              </figcaption>
            </figure>
          </div>
        </div>
      </div>

      {/* ── Discipline rail (full-bleed bottom strip) ────────────────── */}
      <div
        className="border-t"
        style={{
          borderColor: C.brassSoft,
          background: `rgba(22,24,27,0.30)`,
        }}
      >
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-14">
          <ul
            className="flex flex-wrap items-stretch"
            style={{ borderLeft: `1px solid ${C.brassSoft}` }}
          >
            {[
              ["§a", "Employment law"],
              ["§b", "Senior HR judgment"],
              ["§c", "Executive coaching"],
              ["§d", "Sensitive facilitation"],
            ].map(([k, label]) => (
              <li
                key={label}
                className="flex items-baseline gap-3 px-4 sm:px-6 lg:px-8 py-5"
                style={{
                  borderRight: `1px solid ${C.brassSoft}`,
                  flex: "1 1 auto",
                  minWidth: 200,
                }}
              >
                <span
                  className="font-inter text-[9.5px] uppercase font-bold tabular-nums"
                  style={{
                    color: C.accentMetal,
                    letterSpacing: "0.28em",
                  }}
                >
                  {k}
                </span>
                <span
                  className="font-inter text-[11px] uppercase font-semibold"
                  style={{
                    color: C.whiteOnDark,
                    letterSpacing: "0.20em",
                  }}
                >
                  {label}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
